/**
 * ParentWordRequest.jsx
 * ─────────────────────
 * Parent word request page at /vocab/parent/word-requests
 * Protected by the parent PIN gate (enforced at navigation time in ParentDashboard).
 *
 * Phases: input → generating → preview → submitted
 * History list at the bottom; click any row to re-view its preview.
 *
 * Responsive: single-column on all breakpoints, max-w-2xl centred on desktop.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkle, CheckCircle, Warning, CaretRight,
  Clock, X, ArrowClockwise,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomInput }  from "@/components/styleguide/ui/BloomInput";
import { BloomBadge }  from "@/components/styleguide/ui/BloomBadge";
import { Mascot }      from "@/components/styleguide/Mascot";
import { ChildAvatar } from "@/experiences/select-child/ChildAvatar";
import { VocabHeader } from "./VocabHeader";
import TrialEndedModal from "./TrialEndedModal";
import { cn }          from "@/lib/utils";
import { api }         from "./api";

// ── Generating step messages ──────────────────────────────────────────────────

const GEN_STEPS = [
  { label: "Checking the word…",            sub: "Making sure it's right for young learners" },
  { label: "Writing the meaning…",          sub: "In clear, child-friendly language" },
  { label: "Finding synonyms and examples…", sub: "Building the full vocabulary picture" },
  { label: "Painting the illustration…",   sub: "Creating a bespoke image — almost there" },
];

// ── Usage pill ────────────────────────────────────────────────────────────────

function UsagePill({ usage }) {
  if (!usage) return null;
  const pct   = usage.count / usage.limit;
  const state = usage.at_limit ? "full" : pct >= 0.8 ? "warn" : "ok";
  const cls   = {
    ok:   "bg-muted border-border text-muted-foreground",
    warn: "bg-warning/15 border-warning/30 text-warning-ink",
    full: "bg-destructive/10 border-destructive/30 text-destructive",
  }[state];
  const dot = { ok: "bg-muted-foreground", warn: "bg-warning-ink", full: "bg-destructive" }[state];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold", cls)}>
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dot)} />
      {usage.count} / {usage.limit} used
      {usage.resets_at && (
        <span className="font-normal opacity-70"> · resets {usage.resets_at}</span>
      )}
    </span>
  );
}

// ── Word detail card (preview + history re-view) ──────────────────────────────

function WordCard({ data, compact = false }) {
  const DIFF_COLOR = { easy: "success", medium: "warning", hard: "primary" };
  return (
    <div className="space-y-3">
      {/* Image */}
      {data.image_url ? (
        <img
          src={data.image_url}
          alt={data.word}
          className="block w-full rounded-2xl border border-border object-cover object-center"
          style={{ aspectRatio: "1 / 1" }}
        />
      ) : (
        <div className="flex w-full items-center justify-center rounded-2xl border-2 border-dashed border-border bg-muted/30"
             style={{ aspectRatio: "1 / 1" }}>
          <p className="text-sm font-semibold text-muted-foreground">Image pending</p>
        </div>
      )}

      {/* Word + badges */}
      <div>
        <p className="font-display text-2xl font-black capitalize text-foreground">{data.word}</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {data.word_type  && <BloomBadge variant="neutral" className="px-2 py-0.5 text-xs">{data.word_type}</BloomBadge>}
          {data.difficulty && <BloomBadge variant={DIFF_COLOR[data.difficulty] || "neutral"} className="px-2 py-0.5 text-xs">{data.difficulty}</BloomBadge>}
          {data.years?.length > 0 && <BloomBadge variant="neutral" className="px-2 py-0.5 text-xs">Y{data.years.join(", Y")}</BloomBadge>}
          {data.gs_11plus  && <BloomBadge variant="blue" className="px-2 py-0.5 text-xs">11+</BloomBadge>}
        </div>
      </div>

      {/* Meaning */}
      {data.meaning && (
        <p className="text-sm font-semibold leading-relaxed text-foreground/90">{data.meaning}</p>
      )}

      {!compact && (
        <>
          {/* Synonyms + Antonyms */}
          <div className="grid grid-cols-2 gap-3">
            {data.synonyms?.length > 0 && (
              <div>
                <p className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">Synonyms</p>
                <p className="text-xs font-semibold text-foreground/80">{data.synonyms.join(", ")}</p>
              </div>
            )}
            {data.antonyms?.length > 0 && (
              <div>
                <p className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">Antonyms</p>
                <p className="text-xs font-semibold text-foreground/80">{data.antonyms.join(", ")}</p>
              </div>
            )}
          </div>

          {/* Sentences */}
          {data.sentences?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">Example sentences</p>
              {data.sentences.slice(0, 2).map((s, i) => (
                <p key={i} className="rounded-xl bg-muted/50 px-3 py-2 text-xs font-semibold italic text-foreground/80 leading-relaxed">
                  "{s}"
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── History preview modal ─────────────────────────────────────────────────────

function HistoryModal({ item, onClose }) {
  if (!item) return null;
  const data = { ...item.word_data, word: item.word, image_url: item.image_url };
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-foreground/25 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-none p-3 sm:p-6">
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="pointer-events-auto w-full max-w-md max-h-[90svh] overflow-y-auto rounded-3xl bg-card px-5 pb-8 pt-5 shadow-soft-lg"
      >
        <div className="mb-4 flex items-center justify-between">
          <p className="font-display text-base font-black text-foreground">Word preview</p>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Status banner — above content */}
        {item.status === "approved" ? (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-success/10 px-3 py-2.5">
            <CheckCircle size={14} weight="fill" className="shrink-0 text-success-ink" />
            <p className="text-xs font-semibold text-success-ink">Approved — available in the library</p>
          </div>
        ) : (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-warning/10 px-3 py-2.5">
            <Clock size={14} weight="fill" className="shrink-0 text-warning-ink" />
            <p className="text-xs font-semibold text-warning-ink">
              Under review — the Bloom team will make it available within 24 hours.
            </p>
          </div>
        )}

        <WordCard data={data} />
      </motion.div>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ParentWordRequest() {
  const navigate = useNavigate();

  // Phase state machine
  const [phase, setPhase] = useState("input"); // input | generating | preview | submitted

  // Data
  const [inputWord,    setInputWord]    = useState("");
  const [generated,    setGenerated]    = useState(null);   // API response
  const [usage,        setUsage]        = useState(null);
  const [children,     setChildren]     = useState([]);
  const [selectedIds,  setSelectedIds]  = useState([]);
  const [history,      setHistory]      = useState([]);
  const [historyItem,  setHistoryItem]  = useState(null);   // modal target
  const [error,        setError]        = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [showBillingSheet, setShowBillingSheet] = useState(false);

  // Raise-a-query inline form (shown after a generation error)
  const [showQueryForm,   setShowQueryForm]   = useState(false);
  const [queryNote,       setQueryNote]       = useState("");
  const [querySubmitting, setQuerySubmitting] = useState(false);
  const [querySent,       setQuerySent]       = useState(false);

  // Generating animation
  const [genStep, setGenStep] = useState(0);
  const genTimer = useRef(null);

  // ── Load on mount ───────────────────────────────────────────────────────────
  useEffect(() => {
    api("/parent/words/ai/usage").then(setUsage).catch(() => {});
    api("/parent/stats")
      .then((data) => {
        const kids = (data || []).map(({ child }) => child);
        setChildren(kids);
        setSelectedIds(kids.map((c) => c.child_id)); // default all ticked
      })
      .catch(() => {});
    api("/parent/words/requests").then(setHistory).catch(() => {});
  }, []);

  // ── Generating step animation ───────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "generating") {
      setGenStep(0);
      clearInterval(genTimer.current);
      return;
    }
    setGenStep(0);
    const intervals = [6000, 12000, 24000]; // step at 6s, 18s, 42s from start
    let i = 0;
    genTimer.current = setInterval(() => {
      i++;
      setGenStep(Math.min(i, GEN_STEPS.length - 1));
      if (i >= intervals.length) clearInterval(genTimer.current);
    }, intervals[i] || 8000);
    return () => clearInterval(genTimer.current);
  }, [phase]);

  // ── Generate ────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    const word = inputWord.trim();
    if (!word) return;
    setError("");
    setPhase("generating");
    try {
      const result = await api("/parent/words/ai/generate", {
        method: "POST",
        body:   { word },
      });
      if (result.usage) setUsage(result.usage);

      if (result.already_exists) {
        setGenerated({ ...result.existing_word, _note: "already_exists" });
      } else if (result.already_requested) {
        setGenerated({ ...result, _note: "already_requested" });
      } else {
        setGenerated(result);
      }
      setPhase("preview");
    } catch (e) {
      setError(e.message || "Generation failed — please try again.");
      setPhase("input");
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedIds.length) { setError("Select at least one child."); return; }
    setSubmitting(true);
    setError("");
    try {
      await api("/parent/words/submit", {
        method: "POST",
        body: {
          word:      generated.word,
          word_data: {
            word:        generated.word,
            word_type:   generated.word_type,
            meaning:     generated.meaning,
            synonyms:    generated.synonyms,
            antonyms:    generated.antonyms,
            sentences:   generated.sentences,
            difficulty:  generated.difficulty,
            years:       generated.years,
            gs_11plus:   generated.gs_11plus,
            tags:        generated.tags || [],
          },
          image_url: generated.image_url || null,
          child_ids: selectedIds,
        },
      });
      // Refresh history
      api("/parent/words/requests").then(setHistory).catch(() => {});
      setPhase("submitted");
    } catch (e) {
      setError(e.message || "Submission failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleChild = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const resetToInput = () => {
    setPhase("input");
    setInputWord("");
    setGenerated(null);
    setError("");
    setShowQueryForm(false);
    setQueryNote("");
    setQuerySent(false);
  };

  // ── Raise a query about a rejected word ─────────────────────────────────────
  const handleRaiseQuery = async () => {
    if (!queryNote.trim()) return;
    setQuerySubmitting(true);
    try {
      await api("/parent/words/ai/raise-query", {
        method: "POST",
        body: {
          word:         inputWord.trim(),
          parent_note:  queryNote.trim(),
          error_detail: error || undefined,   // the error message shown to the parent
        },
      });
      setQuerySent(true);
      setShowQueryForm(false);
    } catch (e) {
      setError(e.message || "Failed to send query — please try again.");
    } finally {
      setQuerySubmitting(false);
    }
  };

  const atLimit = usage?.at_limit;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-[100svh] bg-background">
      <VocabHeader onSubscribe={() => setShowBillingSheet(true)} />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">

        {/* ── Back + title ── */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border"
            aria-label="Go back"
          >
            <ArrowLeft size={16} weight="bold" />
          </button>
          <div>
            <h1 className="font-display text-2xl font-black tracking-tight text-foreground sm:text-3xl">
              Request a word
            </h1>
            <p className="text-sm font-semibold text-muted-foreground">
              Suggest a word for the Bloom library
            </p>
          </div>
        </div>

        {/* ════════════════════════════════ INPUT ════════════════════════════ */}
        <AnimatePresence mode="wait">
          {phase === "input" && (
            <motion.div key="input" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

              <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <p className="text-sm font-semibold text-foreground/80 leading-relaxed">
                  Enter any English word — we'll generate the meaning, examples
                  and an illustration automatically.
                </p>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <BloomInput
                      label="Word"
                      value={inputWord}
                      onChange={(e) => setInputWord(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !atLimit && handleGenerate()}
                      placeholder="e.g. aptitude"
                      data-testid="word-request-input"
                      autoFocus
                    />
                  </div>
                  <BloomButton
                    leftIcon={Sparkle}
                    onClick={handleGenerate}
                    disabled={!inputWord.trim() || atLimit}
                    className="shrink-0"
                    data-testid="word-request-generate-btn"
                  >
                    Generate
                  </BloomButton>
                </div>

                <div className="mt-3">
                  <UsagePill usage={usage} />
                </div>

                {atLimit && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-semibold text-destructive">
                    <Warning size={14} weight="fill" />
                    Monthly limit reached. Resets {usage?.resets_at}.
                  </div>
                )}

                {/* Error + optional raise-query form */}
                {error && !querySent && (
                  <div className="mt-3">
                    <p className="text-sm font-bold text-destructive">{error}</p>

                    {!showQueryForm && (
                      <button
                        onClick={() => setShowQueryForm(true)}
                        className="mt-2 text-xs font-extrabold text-primary-strong underline underline-offset-2 hover:opacity-80"
                      >
                        Still want this word? Raise a request →
                      </button>
                    )}

                    <AnimatePresence>
                      {showQueryForm && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{   opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 space-y-2.5 rounded-2xl border border-primary/20 bg-accent/30 p-3.5">
                            <p className="text-[10px] font-extrabold uppercase tracking-wide text-primary-strong">
                              Word query
                            </p>
                            <p className="text-xs font-semibold text-muted-foreground">
                              Requested word:{" "}
                              <strong className="text-foreground">{inputWord}</strong>
                            </p>
                            <textarea
                              value={queryNote}
                              onChange={(e) => setQueryNote(e.target.value)}
                              rows={3}
                              placeholder="Tell us why you think this word should be included — the Bloom team will review it…"
                              className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none"
                            />
                            <div className="flex flex-wrap gap-2">
                              <BloomButton
                                size="sm"
                                onClick={handleRaiseQuery}
                                disabled={querySubmitting || !queryNote.trim()}
                              >
                                {querySubmitting ? "Sending…" : "Send to Bloom team"}
                              </BloomButton>
                              <BloomButton
                                size="sm"
                                variant="outline"
                                onClick={() => setShowQueryForm(false)}
                                disabled={querySubmitting}
                              >
                                Cancel
                              </BloomButton>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {querySent && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl bg-success/10 px-3 py-2.5">
                    <CheckCircle size={14} weight="fill" className="shrink-0 text-success-ink" />
                    <p className="text-sm font-semibold text-success-ink">
                      Request sent — the Bloom team will look into it.
                    </p>
                  </div>
                )}
              </div>

              {/* History list */}
              {history.length > 0 && (
                <div className="mt-6">
                  <p className="mb-3 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
                    Previous requests
                  </p>
                  <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
                    {history.map((item, i) => (
                      <button
                        key={item.request_id}
                        onClick={() => setHistoryItem(item)}
                        className={cn(
                          "flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-muted/40",
                          i < history.length - 1 && "border-b border-border",
                        )}
                      >
                        {/* Thumbnail */}
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.word}
                            className="h-10 w-10 shrink-0 rounded-xl border border-border object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted border border-border">
                            <Sparkle size={14} className="text-muted-foreground" />
                          </div>
                        )}

                        <span className="min-w-0 flex-1">
                          <span className="block font-display text-sm font-extrabold capitalize text-foreground">
                            {item.word}
                          </span>
                          <span className="block truncate text-xs font-semibold text-muted-foreground">
                            {item.word_data?.meaning?.split(" ").slice(0, 6).join(" ")}…
                          </span>
                        </span>

                        <BloomBadge
                          variant={item.status === "approved" ? "success" : "warning"}
                          className="shrink-0 px-2 py-0.5 text-[10px] capitalize"
                        >
                          {item.status === "approved" ? "In library" : "Under review"}
                        </BloomBadge>

                        <CaretRight size={14} weight="bold" className="shrink-0 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ═══════════════════════════ GENERATING ══════════════════════════ */}
          {phase === "generating" && (
            <motion.div key="generating" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
                <div className="flex flex-col items-center text-center">

                  {/* Mascot */}
                  <div className="mb-6">
                    <Mascot size={72} mood="happy" animated />
                  </div>

                  <p className="font-display text-lg font-black text-foreground">
                    Working on <span className="text-primary-strong">"{inputWord}"</span>
                  </p>

                  {/* Animated step label */}
                  <motion.p
                    key={genStep}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm font-extrabold text-primary-strong"
                  >
                    {GEN_STEPS[genStep].label}
                  </motion.p>
                  <motion.p
                    key={`sub-${genStep}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-1 text-xs font-semibold text-muted-foreground"
                  >
                    {GEN_STEPS[genStep].sub}
                  </motion.p>

                  {/* Step dots */}
                  <div className="mt-5 flex gap-2">
                    {GEN_STEPS.map((_, i) => (
                      <span
                        key={i}
                        className={cn(
                          "h-2 rounded-full transition-all duration-500",
                          i < genStep  ? "w-5 bg-primary"        : "",
                          i === genStep ? "w-5 bg-primary-strong" : "",
                          i > genStep  ? "w-2 bg-border"         : "",
                        )}
                      />
                    ))}
                  </div>

                  <p className="mt-4 text-xs font-semibold text-muted-foreground">
                    This takes about 30–45 seconds
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* ══════════════════════════════ PREVIEW ══════════════════════════ */}
          {phase === "preview" && generated && (
            <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

              {/* Corrected spelling notice */}
              {generated.corrected && (
                <div className="mb-4 flex items-center gap-2 rounded-2xl border border-success/25 bg-success/10 px-4 py-2.5 text-sm font-semibold text-success-ink">
                  <CheckCircle size={15} weight="fill" />
                  Spell-checked to <strong>"{generated.word}"</strong>
                </div>
              )}

              {/* Already exists notice */}
              {generated._note === "already_exists" && (
                <div className="mb-4 flex items-start gap-2 rounded-2xl border border-blue/25 bg-blue/8 px-4 py-2.5 text-sm font-semibold text-blue-ink">
                  <CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0" />
                  <span>
                    <strong>"{generated.word}"</strong> is already in the Bloom library —
                    your children can learn it now without requesting it.
                  </span>
                </div>
              )}

              {/* Already requested notice */}
              {generated._note === "already_requested" && (
                <div className="mb-4 flex items-start gap-2 rounded-2xl border border-warning/25 bg-warning/10 px-4 py-2.5 text-sm font-semibold text-warning-ink">
                  <Warning size={15} weight="fill" className="mt-0.5 shrink-0" />
                  <span>You've already requested this word — it's under review.</span>
                </div>
              )}

              {/* Word card */}
              <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <WordCard data={generated} />

                {/* Child selection — only shown for genuinely new words */}
                {!generated._note && children.length > 0 && (
                  <div className="mt-5 border-t border-border pt-4">
                    <p className="mb-2 text-xs font-extrabold uppercase tracking-wide text-muted-foreground">
                      Add to priority list for
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      {children.map((child) => {
                        const on = selectedIds.includes(child.child_id);
                        return (
                          <button
                            key={child.child_id}
                            onClick={() => toggleChild(child.child_id)}
                            className={cn(
                              "flex items-center gap-2.5 rounded-2xl border px-3 py-2 text-sm font-semibold transition",
                              on
                                ? "border-primary/40 bg-accent text-primary-strong"
                                : "border-border bg-muted/30 text-muted-foreground",
                            )}
                          >
                            <ChildAvatar variant={child.avatar} className="h-7 w-7" />
                            <span>{child.first_name}</span>
                            <span className={cn(
                              "ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-extrabold",
                              on ? "border-primary bg-primary text-white" : "border-border bg-background",
                            )}>
                              {on ? "✓" : ""}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                      <Clock size={12} />
                      Under review — the Bloom team will make it available within 24 hours.
                    </p>
                  </div>
                )}

                {error && (
                  <p className="mt-3 text-sm font-bold text-destructive">{error}</p>
                )}

                {/* Actions */}
                <div className="mt-5 flex flex-wrap gap-3">
                  {!generated._note && (
                    <BloomButton
                      variant="teal"
                      leftIcon={CheckCircle}
                      onClick={handleSubmit}
                      disabled={submitting || !selectedIds.length}
                      className="flex-1 sm:flex-none"
                    >
                      {submitting ? "Submitting…" : "Submit word"}
                    </BloomButton>
                  )}

                  <BloomButton
                    variant="outline"
                    leftIcon={ArrowClockwise}
                    onClick={resetToInput}
                    className="flex-1 sm:flex-none"
                  >
                    Try another word
                  </BloomButton>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══════════════════════════ SUBMITTED ═══════════════════════════ */}
          {phase === "submitted" && (
            <motion.div key="submitted" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="rounded-3xl border border-border bg-card p-8 shadow-soft text-center">
                <div className="mb-4 flex justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                    <CheckCircle size={36} weight="fill" className="text-success-ink" />
                  </span>
                </div>
                <p className="font-display text-xl font-black text-foreground">Word submitted!</p>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-muted-foreground">
                  <strong className="capitalize text-foreground">{generated?.word}</strong> has been
                  added to your selected children's priority list. They'll see it first when they
                  next start learning.
                </p>

                <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                  <BloomButton
                    leftIcon={Sparkle}
                    onClick={resetToInput}
                  >
                    Request another word
                  </BloomButton>
                  <BloomButton
                    variant="outline"
                    onClick={() => navigate(-1)}
                  >
                    Back to dashboard
                  </BloomButton>
                </div>
              </div>

              {/* Refreshed history */}
              {history.length > 0 && (
                <div className="mt-6">
                  <p className="mb-3 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
                    Your requests
                  </p>
                  <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
                    {history.slice(0, 5).map((item, i) => (
                      <button
                        key={item.request_id}
                        onClick={() => setHistoryItem(item)}
                        className={cn(
                          "flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-muted/40",
                          i < Math.min(history.length, 5) - 1 && "border-b border-border",
                        )}
                      >
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.word}
                            className="h-10 w-10 shrink-0 rounded-xl border border-border object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted border border-border">
                            <Sparkle size={14} className="text-muted-foreground" />
                          </div>
                        )}
                        <span className="flex-1 font-display text-sm font-extrabold capitalize text-foreground">
                          {item.word}
                        </span>
                        <BloomBadge
                          variant={item.status === "approved" ? "success" : "warning"}
                          className="shrink-0 px-2 py-0.5 text-[10px] capitalize"
                        >
                          {item.status === "approved" ? "In library" : "Under review"}
                        </BloomBadge>
                        <CaretRight size={14} weight="bold" className="shrink-0 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* History word preview modal */}
      <AnimatePresence>
        {historyItem && (
          <HistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />
        )}
      </AnimatePresence>

      {/* Billing sheet — opened from header "Upgrade to a plan" */}
      <AnimatePresence>
        {showBillingSheet && (
          <TrialEndedModal
            canDismiss={true}
            onDismiss={() => setShowBillingSheet(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}